/*------------------------------------------------------------------------------
   About      : Local filtering for the NBI Insights list (epic DA-697, II-FE-2)

                All of this runs client-side, because `getInsights` has no
                paging, sort, search or filter params — it takes only
                `utilityDocumentId` and returns the whole pilot partitioned
                three ways. So the screen holds every row and narrows locally.

                Pure by design: the list screen's correctness is almost entirely
                "did the right rows survive the filter", and that is only cheap
                to test if it is a function of (rows, filters, catalog).

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { InsightFilters }        from '../store/insightUISlice'
import { InsightCatalog,
         InsightRecord }         from '../types/insightTypes'
import { deriveInsightValidation,
         overallVerdict }        from './insightValidation'
import { insightCategoryLabel } from '../constants/insightClassification'

/**
 * The record's own `category` string.
 *
 * ⚠️ NOT the appliance's category. This used to resolve
 * `appliances[].category` out of the catalog, which is a different field
 * entirely — quill stores `category` on the insight itself.
 */
export function categoryOf(
  _catalog : InsightCatalog | undefined,
  insight  : InsightRecord,
): string | undefined {
  return insight.category ?? undefined
}

/**
 * Free-text match over the fields a user would plausibly type.
 *
 * Deliberately includes `code` and `insightId` as well as the template text:
 * authors work from the insight sheet, where the code is the identifier they
 * have in hand. Case-insensitive, substring, no tokenization — the row count is
 * bounded by pilot size, so there is nothing to gain from anything cleverer.
 */
export function matchesSearch(insight: InsightRecord, rawTerm: string): boolean {
  const term = rawTerm.trim().toLowerCase()
  if (!term) return true

  const haystack = [
    insight.insightId,
    insight.code,
    insight.templateString,
    insight.templateExample,
  ]

  return haystack.some((field) => !!field && field.toLowerCase().includes(term))
}

/**
 * Applies every active filter. Order is irrelevant — they compose as AND — but
 * search runs last because it is the most expensive.
 *
 * An empty-string filter means "all", so `''` must not be treated as a value to
 * match against; that is why each clause guards on truthiness before comparing.
 */
export function filterInsights(
  rows    : InsightRecord[],
  filters : InsightFilters,
  catalog?: InsightCatalog,
): InsightRecord[] {
  return rows.filter((insight) => {
    if (filters.editStatus && insight.editStatus !== filters.editStatus) return false
    if (filters.type && insight.type !== filters.type) return false

    if (filters.applianceId) {
      // Compare as strings: applianceId 0 (Total) is a real value, so a numeric
      // coercion plus truthiness check would silently drop it.
      if (String(insight.applianceId ?? '') !== filters.applianceId) return false
    }

    if (filters.category && categoryOf(catalog, insight) !== filters.category) return false

    // Derived, not read off `validationState` — that field is free-form JSON
    // that nothing currently writes, so filtering on it alone matched nothing.
    if (filters.validation
        && overallVerdict(deriveInsightValidation(insight, catalog)) !== filters.validation) {
      return false
    }

    return matchesSearch(insight, filters.search)
  })
}

/**
 * Options for the appliance filter, restricted to appliances actually present
 * in this pilot's rows.
 *
 * The full catalog lists every appliance Bidgely models; offering all of them
 * on a pilot that uses six is a dropdown the user has to hunt through, and
 * every extra option is guaranteed to return zero rows.
 */
export function applianceFilterOptions(
  rows    : InsightRecord[],
  catalog?: InsightCatalog,
): { value : string; label : string }[] {
  const present = new Set<number>()
  for (const row of rows) {
    if (row.applianceId !== null && row.applianceId !== undefined) present.add(row.applianceId)
  }

  return [...present]
    .map((applianceId) => ({
      value : String(applianceId),
      label : catalog?.appliances?.find((a) => a.applianceId === applianceId)?.displayName
              ?? String(applianceId),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

/** Type options, likewise restricted to what this pilot actually uses. */
export function typeFilterOptions(
  rows    : InsightRecord[],
  catalog?: InsightCatalog,
): { value : string; label : string }[] {
  const present = new Set<string>()
  for (const row of rows) {
    if (row.type) present.add(row.type)
  }

  return [...present]
    .map((code) => ({
      value : code,
      label : catalog?.types?.find((t) => t.code === code)?.displayName ?? code,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

/** Category options present in this pilot. */
export function categoryFilterOptions(
  rows    : InsightRecord[],
  catalog?: InsightCatalog,
): { value : string; label : string }[] {
  const present = new Set<string>()
  for (const row of rows) {
    const category = categoryOf(catalog, row)
    if (category) present.add(category)
  }

  return [...present]
    .map((code) => ({
      value : code,
      // quill-fe owns the category vocabulary; `applianceCategories` in the
      // catalog is a different field entirely and must not be consulted here.
      label : insightCategoryLabel(code),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}
